import React, { createContext, useContext, useState, ReactNode } from "react";
import { get, post } from "../api/http";
import { auth, googleProvider } from '../firebase';
import {
  signInWithPopup,
  UserCredential,
} from 'firebase/auth';

interface AuthContextType {
    isLoading: boolean;
    user: IUser | null;
    login: (username: string) => void;
    logout: () => void;
    handleAfterLogin: () => void;
    signInWithGoogle: () => void;
    email: string;
    setEmail: (email: string) => void;
    token: string;
    setToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<IUser | null>(null);
    const [ isLoading, setIsLoading] = useState<boolean>(true);
    const [token, setToken] = React.useState('');
	const [email, setEmail] = React.useState('');

    React.useEffect(() => {
        // Check if user is already logged in when the component mounts
        const checkUser = async () => {
            const user = await get<IUser>("/user/profile");
            if (user?.id) {
                setUser(user);
            }
            setIsLoading(false);
        };
        checkUser();
    }, []);
    // Function to handle after login
    // This can be extended to include more complex logic like fetching user data
    // or handling tokens, etc.
    const handleAfterLogin = async () => {
        setIsLoading(true);
        const user = await get<IUser>("/user/profile");
        if (user?.id) {
            setUser(user);
            if (user.subscription === 'active') {
                window.location.href = '/chat';
            } else {
                window.location.href = '/pricing';
            }
        }
        setIsLoading(false);
    }

    const signInWithGoogle = async () => {
        const result = await signInWithPopup(auth, googleProvider);
        if (result?.user) {
            const token = await result.user.getIdToken();
            const user = await post('/auth/firebase', {token}).then(() => handleAfterLogin());
        }
    };

    const login = (username: string) => setUser({} as IUser);
    
    const logout = async () => {
        // Clear user data and redirect to home page
        await post("/auth/logout");
        setUser(null);
        window.location.href = "/";
    };

    return (
        <AuthContext.Provider value={{ isLoading, user, email, token, login, logout, handleAfterLogin, signInWithGoogle, setEmail, setToken }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within AuthProvider");
    return context;
};